import { createFileRoute, Link } from "@tanstack/react-router";
import { loadCustomerList } from "~/utils/content-fns";
import { seo, seoLinks } from "~/utils/seo";
import { LogoBar } from "~/components/blocks/LogoBar";

const FEATURED_SLUGS = [
  "qualtrics",
  "instacart",
  "zscaler",
  "brex",
  "dailypay",
  "digitalocean",
  "pricesmart",
];

const STATS = [
  {
    logo: "/images/zscaler-navy.svg",
    stat: "156 hours",
    copy: "156 hours saved in engineering hire provisioning time",
  },
  {
    logo: "/images/instacart-navy.svg",
    stat: "100%",
    copy: "100% of privileged access moved to automated, policy-based JIT access",
  },
  {
    logo: "/images/qualtrics-navy.svg",
    stat: "<1 hour",
    copy: "To onboard new employees on integrated systems.",
  },
  {
    logo: "/images/brex-navy.svg",
    stat: "400",
    copy: "Ability to automate access configurations using Terraform, including adding 400 entitlements in two days",
  },
];

const CTA_STATS = [
  { stat: "1.5 min", copy: "avg. time to process access requests" },
  { stat: "$95k", copy: "saved in license costs" },
  { stat: "18%", copy: "reduction in overprivileged access" },
];

const LOGO_IMAGES = [
  "/images/qualtrics-navy.svg",
  "/images/ramp-navy.svg",
  "/images/instacart-navy.svg",
  "/images/zscaler-navy.svg",
  "/images/do-navy.svg",
  "/images/doordash-navy.svg",
  "/images/brex-navy.svg",
  "/images/klaviyo-navy.svg",
];

export const Route = createFileRoute("/customers/")({
  loader: () => loadCustomerList(),
  head: () => ({
    meta: seo({
      title: "Customer Stories",
      description:
        "See how leading companies use C1 to secure every identity and simplify access governance.",
      url: "/customers",
    }),
    links: seoLinks({ url: "/customers" }),
  }),
  component: CustomersIndex,
});

function CustomersIndex() {
  const allCustomers = Route.useLoaderData();
  const featured = FEATURED_SLUGS.map((slug) =>
    allCustomers.find((c) => c.slug === slug)
  ).filter(Boolean);

  return (
    <div>
      {/* ── Section 1: Hero ── */}
      <div className="relative bg-teal-dark">
        <img
          src="/images/customers-hero-bg.png"
          alt=""
          className="pointer-events-none absolute right-0 top-0 w-[75%] max-w-[1072px] h-auto"
          loading="eager"
        />
        <div className="relative flex min-h-[340px] items-center px-4 pb-32 pt-16 md:min-h-[400px] md:px-8 md:pb-40 md:pt-20 lg:pt-24">
          <div className="mx-auto max-w-screen-xl w-full">
            <h1 className="font-display text-48 font-bold leading-[1.1] text-white md:text-57 lg:text-69">
              Big Challenges.<br />Incredible Outcomes.
            </h1>
          </div>
        </div>
      </div>

      {/* ── Section 2: Logo Bar ── */}
      <div className="relative z-20 -mt-14">
        <LogoBar images={LOGO_IMAGES} />
      </div>

      {/* ── Section 3: Stats ── */}
      <div className="relative z-10 px-4 py-16 md:px-8 md:py-20">
        <div className="mx-auto max-w-screen-xl">
          <div className="grid grid-cols-1 border border-gray-20 bg-[#F7F7F5] divide-y divide-gray-20 md:grid-cols-4 md:divide-x md:divide-y-0">
            {STATS.map((s, i) => (
              <div key={i} className="p-6 md:p-8 bg-[#F7F7F5]">
                <img
                  src={s.logo}
                  alt=""
                  className="mb-6 h-6 w-auto"
                  loading="lazy"
                />
                <p className="font-display text-33 font-bold text-teal-dark md:text-40">
                  {s.stat}
                </p>
                <p className="mt-3 font-body text-sm leading-relaxed text-gray-80">
                  {s.copy}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Section 4: Customer Story Cards ── */}
      <div className="relative overflow-hidden bg-[#161415] px-4 py-16 md:px-8 md:py-20">
        <img
          src="/images/how-it-works-bg.png"
          alt=""
          className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-full w-auto max-w-none opacity-40"
          loading="lazy"
        />
        <div className="relative mx-auto max-w-screen-xl">
          <h2 className="max-w-2xl font-body text-28 font-normal leading-snug text-white md:text-33">
            Learn how your peers are mastering modern identity governance with
            the power of C1.ai.
          </h2>

          <div className="mt-10 grid gap-5 md:grid-cols-2">
            {featured.map((c) =>
              c ? (
                <Link
                  key={c.slug}
                  to="/customers/$slug"
                  params={{ slug: c.slug }}
                  className="group flex flex-col overflow-hidden border border-white/15 bg-dark-card pb-5 transition-colors hover:bg-white/10"
                >
                  <div className="flex-1 p-5">
                    {c.logo_light && (
                      <img
                        src={c.logo_light}
                        alt=""
                        className="mb-3 h-5 w-auto"
                        loading="lazy"
                      />
                    )}
                    <p className="font-body text-sm font-medium leading-relaxed text-white md:text-base">
                      {c.heading}
                    </p>
                  </div>
                  {c.featured_image && (
                    <div className="relative overflow-hidden">
                      <img
                        src={c.featured_image}
                        alt={c.title}
                        className="w-full h-auto block"
                        loading="lazy"
                      />
                      {/* Play button overlay */}
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-black/50">
                          <svg
                            className="ml-1 h-5 w-5 text-white"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
                          </svg>
                        </div>
                      </div>
                    </div>
                  )}
                </Link>
              ) : null
            )}
          </div>
        </div>
      </div>

      {/* ── Section 5: CTA ── */}
      <div className="relative overflow-hidden bg-teal-dark">
        <img
          src="/images/customers-cta-bg.png"
          alt=""
          className="pointer-events-none absolute right-0 top-1/2 -translate-y-1/2 h-full w-auto max-w-none opacity-60"
          loading="lazy"
        />
        <div className="relative px-4 py-16 md:px-8 md:py-24">
          <div className="mx-auto max-w-screen-xl text-center">
            <span className="chip bg-pink-accent text-dark">
              Hop on board
            </span>
            <h2 className="mx-auto mt-4 max-w-3xl font-display text-28 font-bold text-white md:text-40">
              And join the C1.ai customers seeing measurable results like
            </h2>

            <div className="mx-auto mt-10 grid max-w-4xl grid-cols-1 border border-white/20 divide-y divide-white/20 md:grid-cols-3 md:divide-x md:divide-y-0">
              {CTA_STATS.map((s, i) => (
                <div key={i} className="px-6 py-8 md:py-10">
                  <p className="font-display text-40 font-bold text-white md:text-48">
                    {s.stat}
                  </p>
                  <p className="mt-3 font-body text-sm leading-relaxed text-white/70">
                    {s.copy}
                  </p>
                </div>
              ))}
            </div>

            <div className="mt-10">
              <a
                href="/lp/request-demo"
                className="inline-flex items-center gap-2 rounded-md border border-white bg-white px-8 py-2.5 font-body text-sm font-medium text-dark transition-colors hover:bg-white/90"
              >
                Talk to us to learn how
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
